import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '../shared/services/user.service';
import { NgxDeeplinkerService } from 'ngx-deeplinker';
import { GlobalAlertService } from '../shared/services/global-alert.service';
import { UserDefaults, UserStatus, User, UserType } from '../shared/model/user.model';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { PermissionService } from '../shared/services/permissions.service';
import { BusinessRolesService } from '../shared/services/business-roles.service';
import { UserFormService } from '../shared/services/user-form.service';
import { UserSubmitService } from '../shared/services/user-submit.service';
import { MixPanelService } from '../shared/services/mixpanel.service';
import { ChangePrimaryOrgDialogComponent } from '../dialogs/change-primary-org/change-primary-org-dialog.component';

@Component({
  selector: 'app-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss', '../shared/shared.styles.scss'],
})
export class EditComponent implements OnInit, OnDestroy {
  userForm: FormGroup;
  appPrefix = 'usr';
  selectedId = '';
  isSubmitting = false;
  userStatus$: Observable<UserStatus>;
  subscriptions: Subscription[] = [];
  canSubmitRoles = false;
  canSubmitSubscriptions = false;
  isOnboarding = false;

  readonly mode = 'edit';

  user: User;
  loadingUserDetails = true;
  loggedInUserId: string;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: GlobalAlertService,
    private userService: UserService,
    private ngxDeeplinkerService: NgxDeeplinkerService,
    private permissionService: PermissionService,
    private businessRolesService: BusinessRolesService,
    private userFormService: UserFormService,
    private userSubmitService: UserSubmitService,
    private cdr: ChangeDetectorRef,
    private mixpanelService: MixPanelService,
  ) {
    this.userStatus$ = this.userService.user$.pipe(map((user) => user.status));
    this.userForm = this.userFormService.createUserForm(UserDefaults, true);
    this.userFormService.disableFields(this.userForm, true);
    this.userFormService.disableSyncToEcrm(this.userForm, { ...UserDefaults });
  }

  ngOnInit() {
    const routeSub = this.route.params.subscribe(async (params) => {
      if (params.id) {
        this.selectedId = params.id;
        this.userService.selectedUserId$.next(this.selectedId);
        this.loadUser(this.selectedId);
      }
    });

    const userSub = this.userService.loggedInUser$.subscribe((loggedInUser) => {
      if (loggedInUser && loggedInUser.userId) {
        this.loggedInUserId = loggedInUser.userId;
      }
    });

    const permissionSub = this.permissionService.permissions$.subscribe((permissions) => {
      if (permissions) {
        const isOwnProfile = this.selectedId && this.loggedInUserId && this.selectedId === this.loggedInUserId;

        this.canSubmitRoles = this.permissionService.canSubmitRoles() && !isOwnProfile;
        this.canSubmitSubscriptions =
          this.permissionService.canSubmitRoles() || (isOwnProfile && this.permissionService.canUpdateContacts());

        if (this.permissionService.isOnboarding()) {
          this.isOnboarding = true;
        }
      }
    });

    this.subscriptions.push(...[routeSub, userSub, permissionSub]);
  }

  async loadUser(userId: string) {
    try {
      this.loadingUserDetails = true;
      const userData = await this.userService.getUser(userId);
      this.user = userData;
      this.userForm.patchValue(this.user);
      this.userForm.setControl('phones', this.userFormService.createPhonesFormArray(this.user.phones));
      this.userFormService.initialValidation(this.userForm, this.user);
      this.loadingUserDetails = false;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading user:', error);
      this.messageService.setError('Failed to load user data');
    }
  }

  openingPrimaryOrgDialog = false;

  get isExternalOwnProfile(): boolean {
    const isOwnProfile = !!(this.selectedId && this.loggedInUserId && this.selectedId === this.loggedInUserId);
    const isExternalUser = this.user?.userType === UserType.EXTERNAL;

    return isExternalUser && isOwnProfile;
  }

  openChangePrimaryOrgDialog(): void {
    if (this.openingPrimaryOrgDialog) {
      return;
    }
    // Immediate feedback on the button; defer the (heavier) dialog open to the
    // next tick so the spinner paints first and the click feels responsive.
    this.openingPrimaryOrgDialog = true;
    setTimeout(() => {
      const ref = this.dialog.open(ChangePrimaryOrgDialogComponent, {
        width: '420px',
        disableClose: true,
        data: {
          userId: this.selectedId,
          currentOrgId: this.user?.organization_id,
          currentOrgName: this.user?.organization_name,
        },
      });
      ref.afterOpened().subscribe(() => (this.openingPrimaryOrgDialog = false));
      ref.afterClosed().subscribe((result: { changed?: boolean; newOrgId?: string; newOrgName?: string } | boolean) => {
        this.openingPrimaryOrgDialog = false;
        // When the primary org was changed, sync the edit form + cached user so
        // that exiting edit via Save re-submits the NEW org (not the stale old
        // one, which would silently revert the change).
        if (result && typeof result === 'object' && result.changed) {
          this.userForm.patchValue({
            organization_id: result.newOrgId,
            organization_name: result.newOrgName,
          });
          if (this.user) {
            this.user.organization_id = result.newOrgId;
            this.user.organization_name = result.newOrgName;
          }
        }
      });
    });
  }

  async handleSubmit() {
    this.mixpanelService.saveEditUser();
    this.isSubmitting = true;
    try {
      await this.userSubmitService.handleEditSubmit(
        this.userForm,
        this.selectedId,
        this.canSubmitRoles,
        this.isOnboarding,
        true,
        this.canSubmitSubscriptions,
      );
    } finally {
      this.isSubmitting = false;
    }
  }

  handleCancel() {
    this.ngxDeeplinkerService.returnHandler({ appPrefix: this.appPrefix });
    setTimeout(() => {
      this.businessRolesService.clearNewSpaces$.next(true);
      this.businessRolesService.newSpacesWithRoles$.next([]);
    }, 10);
    if (this.selectedId) {
      this.router.navigate([`details/${this.selectedId}/view`], {});
    } else {
      this.router.navigate(['/']);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
